import type { SessionManager } from './SessionManager.js'
import type { CommerceHttpAdapter } from '../client/CommerceHttpAdapter.js'
import type { CommerceContext } from '../client/CommerceContext.js'
import type { SessionInfo } from '../types/index.js'
import { AltazionApiError } from '../client/errors.js'

/**
 * Gestionnaire de session pour le mode navigateur classique.
 *
 * Responsabilités :
 * - Appel à POST /commerce/api/sessions/create pour créer la session
 * - La session est identifiée côté serveur via le cookie `altz_session_id`
 *   (créé automatiquement par l'API et transmis grâce à credentials:'include')
 */
export class BrowserSessionManager implements SessionManager {
  private readonly http: CommerceHttpAdapter
  private readonly context: CommerceContext

  constructor(http: CommerceHttpAdapter, context: CommerceContext) {
    this.http = http
    this.context = context
  }

  async initialize(): Promise<SessionInfo | null> {
    // Vérifier si une session existe déjà
    try {
      const existing = await this.http.get<SessionInfo>('/commerce/api/sessions/raw')
      return existing
    } catch (err) {
      // 404 = pas de session, on en crée une
      if (err instanceof AltazionApiError && err.status === 404) {
        return this.createSession()
      }
      throw err
    }
  }

  async onSessionExpired(): Promise<void> {
    await this.createSession()
  }

  private async createSession(): Promise<SessionInfo> {
    const body = this.buildCreationRequest()
    return this.http.post<SessionInfo>('/commerce/api/sessions/create', body)
  }

  private buildCreationRequest(): Record<string, unknown> {
    if (this.context.sitePk !== undefined && this.context.rjsId !== undefined) {
      return { sitePk: this.context.sitePk, rjsId: this.context.rjsId }
    }
    if (this.context.siteUrl !== undefined) {
      return { siteUrl: this.context.siteUrl }
    }
    // Fallback : envoyer l'URL courante du navigateur
    const currentUrl = typeof window !== 'undefined' ? window.location.href : undefined
    return { siteUrl: currentUrl }
  }
}
